import { html, property } from 'lit-element';
import { ifDefined } from 'lit-html/directives/if-defined';
import {
  event,
  EventEmitter,
  KatLitMobileElement,
  register,
} from '../../shared/base';
import { KatOption } from '../dropdown/option';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './region-selector.lit.scss';
import getString from './strings';
/**
 * @component {kat-region-selector} KatRegionSelector Dropdown for selecting a supported country/region.
 * @status Production
 * @theme flo
 * @example BasicSingle {"value": "US"}
 * @example BasicMultiple {"multiple": true, "values": "['US', 'BR']"}
 * @example UsingInclude {"include": "['US', 'IN', 'BR', 'AE']"}
 * @example UsingExclude {"exclude": "['US', 'BR']"}
 * @example WithShowClear {"value": "US", "show-clear": true}
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
type SupportedCountry =
  | 'AE'
  | 'AU'
  | 'BE'
  | 'BR'
  | 'CA'
  | 'DE'
  | 'EG'
  | 'ES'
  | 'FR'
  | 'ID'
  | 'IE'
  | 'IN'
  | 'IT'
  | 'JP'
  | 'MX'
  | 'NL'
  | 'PL'
  | 'SA'
  | 'SE'
  | 'SG'
  | 'TH'
  | 'TR'
  | 'UK'
  | 'US'
  | 'ZA';
interface Country {
  id: SupportedCountry;
  name: string;
  icon: string;
  regionId: Region['id'];
}
interface Region {
  id: 'AMER' | 'APAC' | 'EURO' | 'MENA' | 'SSA';
  label: string;
}
export function getSupportedCountries(locale): Country[] {
  return [
    {
      id: 'BR',
      name: getString('region-selector-brazil', null, locale),
      icon: 'flag-br',
      regionId: 'AMER',
    },
    {
      id: 'CA',
      name: getString('region-selector-canada', null, locale),
      icon: 'flag-ca',
      regionId: 'AMER',
    },
    {
      id: 'MX',
      name: getString('region-selector-mexico', null, locale),
      icon: 'flag-mx',
      regionId: 'AMER',
    },
    {
      id: 'US',
      name: getString('region-selector-united-states', null, locale),
      icon: 'flag-us',
      regionId: 'AMER',
    },
    {
      id: 'AU',
      name: getString('region-selector-australia', null, locale),
      icon: 'flag-au',
      regionId: 'APAC',
    },
    {
      id: 'IN',
      name: getString('region-selector-india', null, locale),
      icon: 'flag-in',
      regionId: 'APAC',
    },
    {
      id: 'ID',
      name: getString('region-selector-indonesia', null, locale),
      icon: 'flag-id',
      regionId: 'APAC',
    },
    {
      id: 'JP',
      name: getString('region-selector-japan', null, locale),
      icon: 'flag-jp',
      regionId: 'APAC',
    },
    {
      id: 'SG',
      name: getString('region-selector-singapore', null, locale),
      icon: 'flag-sg',
      regionId: 'APAC',
    },
    {
      id: 'TH',
      name: getString('region-selector-thailand', null, locale),
      icon: 'flag-th',
      regionId: 'APAC',
    },
    {
      id: 'BE',
      name: getString('region-selector-belgium', null, locale),
      icon: 'flag-be',
      regionId: 'EURO',
    },
    {
      id: 'FR',
      name: getString('region-selector-france', null, locale),
      icon: 'flag-fr',
      regionId: 'EURO',
    },
    {
      id: 'DE',
      name: getString('region-selector-germany', null, locale),
      icon: 'flag-de',
      regionId: 'EURO',
    },
    {
      id: 'IE',
      name: getString('region-selector-ireland', null, locale),
      icon: 'flag-ie',
      regionId: 'EURO',
    },
    {
      id: 'IT',
      name: getString('region-selector-italy', null, locale),
      icon: 'flag-it',
      regionId: 'EURO',
    },
    {
      id: 'NL',
      name: getString('region-selector-netherlands', null, locale),
      icon: 'flag-nl',
      regionId: 'EURO',
    },
    {
      id: 'PL',
      name: getString('region-selector-poland', null, locale),
      icon: 'flag-pl',
      regionId: 'EURO',
    },
    {
      id: 'ES',
      name: getString('region-selector-spain', null, locale),
      icon: 'flag-es',
      regionId: 'EURO',
    },
    {
      id: 'SE',
      name: getString('region-selector-sweden', null, locale),
      icon: 'flag-se',
      regionId: 'EURO',
    },
    {
      id: 'TR',
      name: getString('region-selector-turkey', null, locale),
      icon: 'flag-tr',
      regionId: 'EURO',
    },
    {
      id: 'UK',
      name: getString('region-selector-united-kingdom', null, locale),
      icon: 'flag-uk',
      regionId: 'EURO',
    },
    {
      id: 'EG',
      name: getString('region-selector-egypt', null, locale),
      icon: 'flag-eg',
      regionId: 'MENA',
    },
    {
      id: 'SA',
      name: getString('region-selector-saudi-arabia', null, locale),
      icon: 'flag-sa',
      regionId: 'MENA',
    },
    {
      id: 'AE',
      name: getString('region-selector-united-arab-emirates', null, locale),
      icon: 'flag-ae',
      regionId: 'MENA',
    },
    {
      id: 'ZA',
      name: getString('region-selector-south-africa', null, locale),
      icon: 'flag-za',
      regionId: 'SSA',
    },
  ];
}
export function getSupportedRegions(locale): Region[] {
  return [
    {
      id: 'AMER',
      label: getString('region-selector-americas', null, locale),
    },
    {
      id: 'APAC',
      label: getString('region-selector-asia-pacific', null, locale),
    },
    {
      id: 'EURO',
      label: getString('region-selector-europe', null, locale),
    },
    {
      id: 'MENA',
      label: getString(
        'region-selector-middle-east-and-north-africa',
        null,
        locale
      ),
    },
    {
      id: 'SSA',
      label: getString('region-selector-sub-saharan-africa', null, locale),
    },
  ];
}
@register('kat-region-selector')
export class KatRegionSelector extends KatLitMobileElement {
  static get styles() {
    return [baseStyles, styles];
  }

  /** Indicates whether the region selector should allow multiple selections. */
  @property()
  multiple?: boolean = false;

  /** Array of country IDs to include. Unsupported countries are ignored. Cannot be combined with exclude. Defaults to all supported country IDs. */
  @property({ reflect: true })
  include?: SupportedCountry[];

  /** Array of country IDs to exclude. Unsupported countries are ignored. Cannot be combined with include. Defaults to [] */
  @property({ reflect: true })
  exclude?: SupportedCountry[];

  /** Setting disabled stops interactivity. */
  @property()
  disabled?: boolean = false;

  /** Whether single selection shows the clear button. Defaults to `false`. */
  @property({ attribute: 'show-clear' })
  showClear?: boolean = false;

  /**
   * The size of the selected option.
   * @enum {value} large Large select box - Default
   * @enum {value} small Small select box
   */
  @property()
  size?: 'large' | 'small' = 'large';

  /**
   * Setting the state of the input changes its look to give the user more information about what they have entered. This value must be set for State Labels to show up.
   * @enum {value} error Lets the user know there is a problem with the value they have entered in the input.
   */
  @property()
  state?: string;

  /** Optional pretext for the state label. Provides additional context to the state for the input. */
  @property({ attribute: 'state-emphasis' })
  stateEmphasis?: string;

  /** Provides users with more information about why the input is in the state it is in. */
  @property({ attribute: 'state-label' })
  stateLabel?: string;

  /** Whether the component is currently showing its dropdown options. */
  @property()
  expanded?: boolean = false;

  /**
   * The max height of the selection area.
   * This should be a css value with units, e.g. "8px".
   */
  @property({ attribute: 'max-height' })
  maxHeight?: string;

  /** Label used on mobile view. */
  @property({ attribute: 'mobile-label' })
  mobileLabel?: string;

  /**
   * Locale to identify the language used in fixed labels.
   * Use this property/attribute to override the automatic language detection.
   * @type {string}
   */
  @property({ reflect: true })
  locale?: string;

  /** The value of the selected option. Propagated from the internal KatDropdown. */
  @property({ reflect: true })
  value?: string;

  /** The value of the selected options if multiple. Propagated from the internal KatDropdown. */
  @property({ type: Array, reflect: true })
  values?: string[];

  /**The value of the label option. This is the label for the kat-dropdown */
  @property()
  label?: string;

  /** Emits after the region-selector value has changed */
  @event('change', true)
  private _change: EventEmitter<{ value: string; values?: string[] }>;

  /** Emits whenever the region-selector is expanded. */
  @event('expand')
  private _expand: EventEmitter;

  /** Emits whenever the region-selector is collapsed. */
  @event('collapse')
  private _collapse: EventEmitter;

  constructor() {
    super();
    this.updateOptions();
  }

  // Updates when the options are filtered OR a value is set
  shouldUpdate(changingProperties: Map<string, any>): boolean {
    const hasIncludeOrExclude =
      changingProperties.has('include') || changingProperties.has('exclude');
    if (hasIncludeOrExclude || changingProperties.has('locale')) {
      this.updateOptions();
    }
    // Stop an invalid value from being set
    if (changingProperties.has('value') && this.isValueInvalid()) {
      this.value = changingProperties.get('value');
    }
    // filter out invalid values from being set
    if (changingProperties.has('values')) {
      this.values = this.validateValues();
    }
    return true;
  }

  // Clears value/values if updated include/exclude makes it invalid
  updated(changedProperties: Map<string, any>) {
    const hasIncludeOrExclude =
      changedProperties.has('include') || changedProperties.has('exclude');
    if (hasIncludeOrExclude) {
      if (this.multiple) {
        this.values = this.validateValues();
      } else if (this.isValueInvalid()) {
        this.value = '';
      }
    }
  }

  /**
   * Used for single select only.
   * @returns Boolean value for validity of selected option; controlled by selected option's presence in available options list.
   */
  private isValueInvalid(): boolean {
    if (!this.value) {
      return false;
    }
    if (this.multiple) {
      return true;
    }
    return !this.availableCountries.some(
      availableCountry => availableCountry.id === this.value
    );
  }

  /**
   * Used for multi-select only.
   * @returns The list of valid selected country ids from those that have been attempted to be selected.
   */
  private validateValues(): string[] {
    if (!this.values || !this.multiple) {
      return [];
    }

    return this.values.filter(value =>
      this.availableCountries.find(country => country.id === value)
    );
  }

  private availableCountries: Country[];
  private availableRegions: Region[];

  private updateOptions() {
    // https://issues.amazon.com/issues/KAT-11493
    const hasInclude = !!this.include;
    const hasExclude = this.exclude?.length > 0;
    const supportedCountries = getSupportedCountries(this.locale);
    this.availableRegions = getSupportedRegions(this.locale);

    if (hasInclude && hasExclude) {
      console.warn(
        'Using both `include` and `exclude` attributes is not supported.'
      );
    }
    if (hasInclude) {
      this.availableCountries = supportedCountries.filter(country =>
        this.include.includes(country.id)
      );
    } else if (hasExclude) {
      this.availableCountries = supportedCountries.filter(
        country => !this.exclude.includes(country.id)
      );
    } else {
      this.availableCountries = supportedCountries;
    }
  }

  private handleChange(e: { detail: { value?: string; values?: string[] } }) {
    if (e.detail.value) {
      this.value = e.detail.value;
    } else {
      this.values = e.detail.values;
    }
    this._change.emit({ value: this.value, values: this.values });
  }

  private handleExpand() {
    this.expanded = true;
    this._expand.emit();
  }

  private handleCollapse() {
    this.expanded = false;
    this._collapse.emit();
  }

  private selectionSummaryOverride(option: KatOption) {
    return option.value;
  }

  private renderCountry(country: Country) {
    return html`
      <kat-option value=${country.id}>
        <div class="country-option">
          <kat-icon
            class="country-icon"
            name=${country.icon}
            size="small"
          ></kat-icon>
          <span class="country-name">${country.name}</span>
          <span class="country-code">(${country.id.toUpperCase()})</span>
        </div>
      </kat-option>
    `;
  }

  private renderRegion(regionId: Region['id']) {
    const availableCountriesInRegion: Country[] = [];
    this.availableCountries.forEach(country => {
      if (country.regionId === regionId) {
        availableCountriesInRegion.push(country);
      }
    });

    const renderedCountriesInRegion = availableCountriesInRegion.map(country =>
      this.renderCountry(country)
    );

    const regionLabel = this.availableRegions.find(
      region => region.id === regionId
    ).label;

    return html`
      <kat-optgroup>
        <span slot="label">${regionLabel}</span>
        ${renderedCountriesInRegion}
      </kat-optgroup>
    `;
  }

  render() {
    const regionsAvailable: Region['id'][] = [];

    this.availableCountries.forEach(country => {
      if (!regionsAvailable.includes(country.regionId)) {
        regionsAvailable.push(
          this.availableRegions.find(region => region.id === country.regionId)
            .id
        );
      }
    });

    const placeholderString = getString(
      'region-selector-placeholder',
      null,
      this.locale,
      {
        quantity: this.availableCountries.length === 1 ? 'one' : 'other',
        replacers: [['0', this.availableCountries.length.toString()]],
      }
    );

    const renderedRegions = regionsAvailable.map(region =>
      this.renderRegion(region)
    );

    return html`
      <kat-dropdown
        searchable
        include-exportparts
        placeholder=${this.value || this.values?.length > 0
          ? ''
          : placeholderString}
        mobile-label=${ifDefined(this.mobileLabel)}
        label=${ifDefined(this.label)}
        locale=${ifDefined(this.locale)}
        ?multiple-show-apply=${this.multiple}
        ?multiple-sticky-apply=${this.multiple}
        ?multiple=${this.multiple}
        ?rich-selection-label=${!this.multiple}
        ?disabled=${this.disabled}
        ?expanded=${this.expanded}
        ?show-clear=${this.showClear}
        size=${this.size}
        .value=${this.value}
        .values=${this.values}
        .state=${this.state}
        .stateEmphasis=${this.stateEmphasis}
        .stateLabel=${this.stateLabel}
        .maxHeight=${this.maxHeight}
        .selectionSummaryOverride=${this.selectionSummaryOverride}
        @change=${this.handleChange}
        @collapse=${this.handleCollapse}
        @expand=${this.handleExpand}
      >
        <div>${renderedRegions}</div>
        <div></div>
        <!-- I'm not sure why this div is required, but options render outside the dropdown without it -->
      </kat-dropdown>
    `;
  }
}
